<template>
    <div>
        <div class="page-content">
            <b-row class="my-3 align-items-center">
                <b-col md="7">
                    <div v-if="clientName">
                        <h1 class="mb-0">{{ clientName }}</h1>
                        <SunbitStatusBadge
                            :client-id="clientId"
                            :is-force-show="true"
                        />
                    </div>
                    <div v-else class="name-skeleton"></div>
                </b-col>
                <b-col v-if="clientName" class="text-right">
                    <div v-if="editMode"></div>
                    <view-change-log
                        v-else
                        @view-change-log="receiveEmitFromViewChangeLog"
                    ></view-change-log>
                </b-col>
            </b-row>
            <b-card footer-class="p-0" class="rounded-lg" :key="componentKey">
                <client-details-edit
                    :key="editKey"
                    v-if="editMode"
                    ref="clientEditForm"
                    v-on="$listeners"
                />
                <div v-else>
                    <client-change-log
                        v-if="viewChangeLog == true"
                        @view-change-log="receiveEmitFromViewChangeLog"
                    />
                    <client-details-display v-else v-on="$listeners" />
                </div>

                <template
                    v-slot:footer
                    no-body
                    v-if="dataLoaded && !isBusinessLoading"
                >
                    <b-tabs
                        card
                        class="list tab-table-responsive"
                        lazy
                        v-if="clientId"
                        v-model="activeTabIndex"
                    >
                        <tab-relationships
                            v-if="_hasAccess('client.patient.view')"
                            :key="1 + keyComponent"
                            @export-patient="exportPatient"
                        />
                        <tab-contact-details
                            v-if="_hasAccess('client.patient.view')"
                            :key="2 + keyComponent"
                            @primary-assigned="reloadContactDetails(true)"
                            @open-contact-modal="openContactDetailForm"
                        />
                        <tab-documents
                            :key="3 + keyComponent"
                            :clientName="clientName"
                        />
                        <tab-billing
                            v-if="_hasAccess('client.billing.view')"
                            :key="4 + keyComponent"
                            :open-list="billingList"
                        />
                        <tab-communications
                            v-if="_hasAccess('client.communication.view')"
                            :key="5 + keyComponent"
                        />
                        <tab-appointments
                            :key="6 + keyComponent"
                            @create-appointment="createAppointment"
                        />
                        <tab-reminders
                            v-if="_hasAccess('client.reminder.view')"
                            :client="clientDetails"
                            :key="7 + keyComponent"
                        />
                        <tab-notes
                            v-if="_hasAccess('client.notes.view')"
                            :key="8 + keyComponent"
                        />
                        <tab-tasks :key="9 + keyComponent" />
                    </b-tabs>
                </template>
            </b-card>
        </div>

        <div v-if="clientId && !isBusinessLoading">
            <modal-log-communication
                :module="COMMUNICATION.MODULE_CLIENT"
                ref="modalLogCommunication"
                :client-id="clientId"
                @onsuccess="reloadTab(4)"
            />
            <modal-send-sms
                ref="modalSendSms"
                :module="COMMUNICATION.MODULE_CLIENT"
                :client-details="clientDetails"
                @sent="postSendSms"
            />
            <modal-send-email
                ref="modalSendEmail"
                :module="COMMUNICATION.MODULE_CLIENT"
                :client-details="clientDetails"
                @onSendSuccess="reloadTab(4)"
            />
            <modal-new-letter ref="modalNewLetter" :redirect="true" />
            <modal-new-attachment
                ref="modalNewAttachment"
                @reload="reloadTab(2)"
            />
            <modal-new-webform ref="modalNewWebform" :client="clientDetails" />
            <modal-new-relationship
                ref="modalNewRelationship"
                @reload="reloadTab(0)"
            />
            <modal-new-note ref="modalNewNote" @reload="reloadTab(7)" />
            <modal-send-statement
                ref="modalEmailStatement"
                :client="clientDetails"
                :is-email="true"
            />
            <modal-send-statement
                ref="modalPrintStatement"
                :client="clientDetails"
                :is-email="false"
            />
            <modal-new-payment-method
                ref="modalNewPaymentMethod"
                @reload="reloadTab(3, 'PAYMENT_METHODS')"
            />
            <modal-new-estimate
                ref="modalNewEstimate"
                :client="clientDetails"
            />
            <modal-new-reminder
                ref="modalNewReminder"
                :client-id="clientId"
                @reload="reloadTab(6)"
            />
            <modal-new-task
                ref="modalAddNewTask"
                module="client"
                :current-client="clientDetails"
                @reload="reloadTab(8)"
            />

            <modal-new-reminder-bundle
                ref="modalNewReminderBundle"
                :clientId="clientId"
                :module="'client'"
                @reload="reloadTab(6)"
            />
            <modal-new-invoice ref="modalNewInvoice" :client="clientDetails" />
            <modal-appointment ref="modalAppointment" @reload="reloadTab(5)" />
            <modal-export ref="modalExport" :clientDetails="clientDetails" />
            <popup-notes :clientId="clientId" :key="popupKey" />
            <modal-new-edit-contact-detail
                ref="modalNewEditContactDetail"
                @onsuccess="reloadContactDetails"
            />
        </div>
    </div>
</template>
<style type="text/css" scoped>
.name-skeleton {
    height: 33px;
    margin: 16px 24px;
}
</style>
<script>
import { clone, isEmpty } from "lodash";
import ClientDetailsDisplay from "@/components/client-details/details-display";
import ClientDetailsEdit from "@/components/client-details/details-edit";
import TabRelationships from "./tabs/tab-relationships";
import TabContactDetails from "./tabs/tab-contact-details";
import TabDocuments from "./tabs/tab-documents";
import TabBilling from "./tabs/tab-billing";
import TabCommunications from "./tabs/tab-communications";
import TabAppointments from "./tabs/tab-appointments";
import TabReminders from "./tabs/tab-reminders";
import TabNotes from "./tabs/tab-notes";
import TabTasks from "./tabs/tab-tasks";
import ModalLogCommunication from "@/components/communication/communication-log-modal";
import ModalSendSms from "@/components/communication/communication-send-sms";
import ModalSendEmail from "@/components/communication/communication-send-email";
import ModalNewLetter from "@/components/client-documents/document-add-letter-modal";
import ModalNewAttachment from "@/components/client-documents/document-add-attachment-modal";
import ModalNewWebform from "@/components/webform/modal-new-webform";
import ModalNewRelationship from "@/components/client-relationships/relationship-new-modal";
import ModalNewNote from "@/components/client-notes/note-add-modal";
import ModalSendStatement from "@/components/client-statements/statement-send";
import ModalNewPaymentMethod from "@/components/payment-methods/payment-method-save";
import ModalNewEstimate from "@/components/client-estimates/estimate-new-modal";
import ModalNewReminder from "@/components/client-reminders/reminder-save-modal";
import ModalNewTask from "@/components/tasks/task-update-modal";
import ModalNewInvoice from "@/components/new-invoice/invoice-modal";
import ModalNewEditContactDetail from "@/components/client-contact-details/detail-new-edit-modal";
import PopupNotes from "@/components/client/popup-notes";
import unsavePrompt from "@/mixins/unsave-prompt.js";
import ModalAppointment from "@/module/schedule/appointment/create-update/modal";
import { DateTime } from "luxon";
import modalNewReminderBundle from "@/components/reminder-bundle/modal";
import COMMUNICATION from "@/constants/communications";
import COMMUNICATIONTYPE from "@/constants/communication-types";
import ModalExport from "@/components/client/modal-export";
import ViewChangeLog from "./view-change-log.vue";
import ClientChangeLog from "@/components/client-details/client-change-log";
import SunbitStatusBadge from "@/components/payments/SunbitStatusBadge.vue";

export default {
    components: {
        ClientDetailsDisplay,
        ClientDetailsEdit,
        TabRelationships,
        TabContactDetails,
        TabDocuments,
        TabBilling,
        TabCommunications,
        TabAppointments,
        TabReminders,
        TabNotes,
        TabTasks,
        ModalLogCommunication,
        ModalSendSms,
        ModalSendEmail,
        ModalNewLetter,
        ModalNewAttachment,
        ModalNewRelationship,
        ModalSendStatement,
        ModalNewNote,
        ModalNewPaymentMethod,
        ModalNewEstimate,
        ModalNewReminder,
        ModalNewTask,
        ModalNewInvoice,
        ModalAppointment,
        PopupNotes,
        modalNewReminderBundle,
        ModalNewWebform,
        ModalExport,
        ViewChangeLog,
        ClientChangeLog,
        ModalNewEditContactDetail,
        SunbitStatusBadge
    },
    data() {
        return {
            COMMUNICATION: COMMUNICATION,
            componentKey: 0,
            activeTabIndex: 0,
            keyComponent: 0,
            billingList: null,
            editKey: 0,
            viewChangeLog: false
        };
    },
    mixins: [unsavePrompt],
    computed: {
        switch_text() {
            return this.$root.setting.schedule_view
                ? "Switch to list view"
                : "Switch to classic view";
        },
        clientId() {
            return this.$store.state.client.client_id;
        },
        editMode() {
            // this.editKey++;
            return this.$store.state.client.editMode;
        },
        clientName() {
            if (this.isBusinessLoading) {
                return "Loading...";
            }

            let client = this.$store.state.client.client_details;
            let clientName = isEmpty(client)
                ? null
                : this.capitalizeEachWord(client.firstname) +
                  " " +
                  this.capitalizeEachWord(client.lastname) +
                  " (" +
                  client.index +
                  ")";
            return clientName;
        },
        routeParam() {
            return this.$route.params.id;
        },
        clientDetails() {
            return this.$store.getters["client/clientDetails"];
        },
        popupKey() {
            return this.$store.getters["client/popupKey"];
        },
        dataLoaded() {
            return !isEmpty(this.$root.permissions);
        },
        form_dirty() {
            if (this.editMode && this.$refs.clientEditForm.isDirty()) {
                return true;
            }

            if (this.$store.getters["client/isDirty"]) {
                return true;
            }

            return false;
        },
        clientAndEditPermissions() {
            return (
                !isEmpty(this.clientDetails) &&
                this.$root.permissions["client.profile.update"] == 1
            );
        },
        isBusinessLoading() {
            return this.$store.getters["isBusinessLoading"];
        }
    },
    watch: {
        routeParam() {
            this.componentKey++;
            this.viewChangeLog = false;
            this.getDetails();
        },
        isBusinessLoading() {
            let client = this.$store.state.client.client_details;
            let isClientNotExist = isEmpty(client);

            if (!this.isBusinessLoading && isClientNotExist) {
                this.$router.push({ name: "client_dashboard" });
            }
        },
        clientAndEditPermissions: {
            immediate: true,
            handler(value) {
                if (value && this.$route.query.isEditMode == "true") {
                    this.$store.commit("client/setEditMode", true);
                }
            }
        }
    },
    mounted() {
        this.getDetails();
        this.$root.$on("resend-communication", this.resendCommunication);
    },
    beforeDestroy: function () {
        this.$root.$off("resend-communication");
    },
    methods: {
        resendCommunication(comm) {
            switch (comm.type) {
                case COMMUNICATIONTYPE.COMMUNICATION_TYPES.SMS: {
                    // SMS
                    this.$refs.modalSendSms.open(comm);
                    break;
                }

                case COMMUNICATIONTYPE.COMMUNICATION_TYPES.EMAIL: {
                    // email
                    this.$refs.modalSendEmail.open(comm);
                    break;
                }
            }
        },
        createAppointment() {
            const {
                firstname,
                firstName,
                lastname,
                lastName
            } = this.clientDetails;

            let fullName = `${firstname || firstName} ${lastname || lastName}`;

            let client = {
                ...this.clientDetails,
                label: fullName // append "label" key
            };

            client.statusClient = client.status; // clone before delete
            delete client.status; // need to delete this, error when saving appointment(?)

            this.$refs.modalAppointment.open({
                referrer: "client-profile",
                id: "create-appointment",
                is_new: true,
                allDay: false,
                start: DateTime.local()
                    .startOf("hour")
                    .plus({ hour: 1 })
                    .toJSDate(),
                client: client
            });
        },
        async getDetails() {
            var self = this;
            var encodedId = this.$route.params.id;
            var index = this.$route.params.index;

            this.$store.commit("client/setClientDetails", {});
            this.$store.commit("client/setClientId", null);

            if (encodedId || index) {
                this.$store.commit("client/clearPopupNotes"); // clear popup notes by default
                this.$store.commit("client/setEditMode", false);
                this.$store
                    .dispatch("client/getClientDetails", {
                        id: index || encodedId,
                        encoded: index
                            ? 0
                            : this.$route.query.encoded === "0"
                            ? 0
                            : 1,
                        index: index ? 1 : 0
                    })
                    .then(hasData => {
                        if (hasData) {
                            setTimeout(() => {
                                self.storeAudit();
                            }, 1000);
                            self.generatePageTitle();
                        } else if (!this.isBusinessLoading) {
                            if (hasData.no_access) {
                                //no access when checking in PreventRestrictedUser middleware
                                self._message(hasData.message, "danger");
                                self.redirectToDashboard();
                            } else {
                                self.redirectToDashboard();
                            }
                        }
                    });
            } else {
                self.redirectToDashboard();
            }
        },
        async storeAudit() {
            const clientId = clone(this.clientDetails.id);
            const fullPath = clone(this.$router.currentRoute.fullPath);

            if (!clientId) {
                return;
            }

            this._getSetting().then(() => {
                this._saveAudit({
                    action: "view",
                    path: "/client/client/view." + clientId,
                    reference_id: clientId,
                    fullPath
                });
            });
        },
        exportAppointment(exportType) {
            this.$refs.exportAppointmentWindow.open(exportType);
        },
        boardingEmail() {
            this.$refs["boardingReservationWindow"].open(true);
        },
        boardingPrint() {
            this.$refs["boardingReservationWindow"].open(false);
        },
        boardingForm() {
            this.$refs["boardingFormWindow"].open();
        },
        reloadTab(tabIndex, billingList) {
            this.billingList = billingList ? billingList : null;
            this.activeTabIndex = tabIndex;
            this.keyComponent++;
        },
        postSendSms(isChangeNumber = false, phoneNumber) {
            this.reloadTab(4);

            if (isChangeNumber) {
                this.$store.commit(
                    "client/setClientDetailPhoneNumber",
                    phoneNumber.mobileNumber
                );
            }
        },
        capitalizeEachWord(string) {
            return string.replace(/(^\w{1})|(\s+\w{1})/g, letter =>
                letter.toUpperCase()
            );
        },
        onSuccessCreateReminderBundle() {
            this.$store.dispatch("patient/getReminders", this.patientId);
            this.reloadTab("tabReminders");
        },
        generatePageTitle() {
            let client = this.$store.state.client.client_details;
            this.dynamicPageTitle(
                isEmpty(client)
                    ? "Client Profile"
                    : this.capitalizeEachWord(client.firstname) +
                          " " +
                          this.capitalizeEachWord(client.lastname)
            );
        },
        exportPatient() {
            this.$refs.modalExport.open();
        },
        receiveEmitFromViewChangeLog(value) {
            this.viewChangeLog = value;
        },
        redirectToDashboard() {
            this.$router.push({ name: "client_dashboard" });
        },
        async reloadContactDetails(isPrimary = false) {
            if (isPrimary) {
                await this.$store.dispatch("client/getClientDetails", {
                    id: this.clientId
                });
            }

            if (!isPrimary || this.activeTabIndex !== 1) {
                this.reloadTab(1);
            }
        },
        openContactDetailForm(contact = {}) {
            this.$refs.modalNewEditContactDetail.open(contact);
        }
    }
};
</script>
